import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { uploadToBucket } from "@/lib/uploads";
import { toast } from "sonner";
import { Plus, FileText, Video, Link as LinkIcon, Image as ImageIcon, Trash2, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/teacher/resources")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Resources — MAKAI" }] }),
  component: ResourcesPage,
});

const KIND_ICON: Record<string, any> = { pdf: FileText, video: Video, image: ImageIcon, link: LinkIcon, document: FileText, note: FileText, other: FileText };

function ResourcesPage() {
  const ctx = useTeacherContext();
  const [items, setItems] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    if (!ctx.staffId) return;
    const { data } = await supabase
      .from("teaching_resources")
      .select("id,title,description,kind,url,storage_path,visibility,subject_id,stream_id,subject:subjects_catalog(name),stream:streams(name,class:classes(name))")
      .eq("teacher_id", ctx.staffId).order("created_at", { ascending: false });
    setItems(data ?? []);
  };
  useEffect(() => { if (!ctx.loading) load(); }, [ctx.loading, ctx.staffId]);

  const filtered = items.filter((r) => !q || r.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppShell role="teacher" title="Resources" subtitle="Upload teaching materials and share with your learners."
      action={<Button onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" /> Add resource</Button>}>
      <div className="mb-3"><Input placeholder="Search resources…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No resources yet.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((r) => {
            const Icon = KIND_ICON[r.kind] ?? FileText;
            return (
              <div key={r.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
                <div className="flex items-start gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary"><Icon className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-display font-bold">{r.title}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {r.kind} · {r.visibility}
                      {r.subject?.name ? ` · ${r.subject.name}` : ""}
                      {r.stream ? ` · ${r.stream.class?.name} ${r.stream.name}` : ""}
                    </div>
                    {r.description && <p className="mt-1 line-clamp-2 text-xs text-foreground/80">{r.description}</p>}
                  </div>
                </div>
                <div className="mt-3 flex justify-between">
                  <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                    Open <ExternalLink className="h-3 w-3" />
                  </a>
                  <Button variant="ghost" size="sm" onClick={async () => {
                    if (!confirm("Delete this resource?")) return;
                    await supabase.from("teaching_resources").delete().eq("id", r.id);
                    load();
                  }}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ResourceDialog open={open} onOpenChange={setOpen} ctx={ctx} onSaved={load} />
    </AppShell>
  );
}

function ResourceDialog({ open, onOpenChange, ctx, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ title: "", description: "", kind: "pdf", url: "", visibility: "class", subject_id: "", stream_id: "" });
      setFile(null);
    }
  }, [open]);

  async function save() {
    if (!form.title) { toast.error("Title required"); return; }
    if (form.kind !== "link" && !file && !form.url) { toast.error("Upload a file or provide URL"); return; }
    setSaving(true);
    try {
      let url = form.url; let storage_path: string | null = null; let size_bytes: number | null = null; let kind = form.kind;
      if (file) {
        const up = await uploadToBucket("teacher-files", file, "resources");
        url = up.url; storage_path = up.path; size_bytes = up.size; kind = (up.kind === "file" ? "other" : up.kind);
      }
      const { error } = await supabase.from("teaching_resources").insert({
        school_id: ctx.schoolId, teacher_id: ctx.staffId,
        title: form.title, description: form.description || null,
        kind, url, storage_path, size_bytes,
        visibility: form.visibility,
        subject_id: form.subject_id || null, stream_id: form.stream_id || null,
      });
      if (error) throw error;
      toast.success("Resource added");
      onOpenChange(false); onSaved();
    } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  }

  const subjectsForStream = form.stream_id
    ? ctx.assignments.filter((a: any) => a.stream.id === form.stream_id && a.subject).map((a: any) => a.subject)
    : ctx.subjects;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Add resource</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Input placeholder="Title" value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Textarea placeholder="Description (optional)" value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <select value={form.stream_id} onChange={(e) => setForm({ ...form, stream_id: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="">All classes</option>
              {ctx.streams.map((s: any) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
            </select>
            <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="">All subjects</option>
              {subjectsForStream.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              {["pdf","video","image","link","document","note","other"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="private">Private (only me)</option>
              <option value="class">My class learners</option>
              <option value="school">Whole school</option>
            </select>
          </div>
          {form.kind === "link" ? (
            <Input placeholder="https://…" value={form.url ?? ""} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          ) : (
            <div>
              <label className="text-sm font-medium">Upload file</label>
              <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="mt-1 block w-full text-sm" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
